import { FC, ReactNode, useCallback } from 'react'
import { useTranslate } from 'react-polyglot'
import { useMessaging } from '@ucc/admin-messaging'
import { useReportTranslations } from '@ucc/unified-admin-common'
import { useSnackbar } from 'notistack'

import Snackbar from 'components/SnackBar'
import { useLiveJobs } from 'hooks'
import { Job } from 'types/Job'
import { SNACKBAR_DURATION } from 'utils/constants'
import { QUALTRICS_SURVEY_TRIGGER_TYPES } from 'utils/constants'
import { getJobActivity } from 'utils/jobs/getJobActivity'
import { DEFAULT_NOTIFICATION, ERROR_NOTIFICATION, INFO_NOTIFICATION, SUCCESS_NOTIFICATION } from 'utils/notifications'
import { triggerQualtricsSurvey } from 'utils/qualtricsSurvey'

interface GlobalNotificationsProviderProps {
  children: ReactNode
}

const variants = {
  default: DEFAULT_NOTIFICATION,
  success: SUCCESS_NOTIFICATION,
  info: INFO_NOTIFICATION,
  error: ERROR_NOTIFICATION,
}

const GlobalNotificationsProvider: FC<GlobalNotificationsProviderProps> = ({ children }) => {
  const t = useTranslate()
  const reportTranslations = useReportTranslations()
  const { enqueueSnackbar } = useSnackbar()

  const jobCallback = useCallback(
    (job: Job) => {
      const activity: any = getJobActivity(job, t, reportTranslations)
      const endTime = activity?.endTime || 0

      if (activity && endTime > Date.now() - SNACKBAR_DURATION) {
        const variant = activity.isSuccessful ? 'success' : 'error'
        activity.message !== '' &&
          enqueueSnackbar(activity.message, {
            ...variants[variant],
            autoHideDuration: SNACKBAR_DURATION,
            content: (key) => <Snackbar id={key} message={activity.message ?? ''}></Snackbar>,
          })

        if (!sessionStorage.getItem('qualtricsSurveyWasCalled')) {
          const surveyType =
            activity?.isSuccessful && activity?.status === 'COMPLETED'
              ? QUALTRICS_SURVEY_TRIGGER_TYPES.success
              : QUALTRICS_SURVEY_TRIGGER_TYPES.error
          triggerQualtricsSurvey(surveyType, activity)
        }
      }
    },
    [enqueueSnackbar, t, reportTranslations],
  )

  useLiveJobs(jobCallback)

  useMessaging('snackbarNotification', (payload: unknown) => {
    const safeGlobalNotification = { ...(payload as Record<string, any>) }
    const description = safeGlobalNotification.description
    delete safeGlobalNotification['description']
    delete safeGlobalNotification['content']
    delete safeGlobalNotification['action']
    enqueueSnackbar(safeGlobalNotification.message, {
      ...safeGlobalNotification,
      ...variants[safeGlobalNotification.variant || 'default'],
      autoHideDuration: SNACKBAR_DURATION,
      content: (key) => (
        <Snackbar
          id={key}
          message={safeGlobalNotification.message}
          isPersistent={safeGlobalNotification.persist}
          description={description}
        />
      ),
    })
  })

  return <>{children}</>
}

export default GlobalNotificationsProvider
